// SSE client over fetch streaming (RF-UI-15, AC-73). fetch is used instead of
// EventSource so the client can send `Last-Event-ID` on its own reconnects (cursor resume),
// apply exponential backoff with jitter, and observe stream closure precisely.
import type { EventEnvelope } from "@maestros/contracts";
import { buildUrl } from "../api/client.ts";

export type StreamStatus = "connecting" | "live" | "reconnecting" | "offline";

export interface SseParsedEvent {
  id: string | null;
  event: string;
  data: string;
}

/** Incremental text/event-stream parser (WHATWG HTML §9.2). */
export class SseParser {
  private buffer = "";
  private data: string[] = [];
  private event = "";
  private id: string | null = null;

  push(chunk: string, emit: (e: SseParsedEvent) => void, onRetry?: (ms: number) => void): void {
    this.buffer += chunk;
    let idx: number;
    while ((idx = this.buffer.search(/\r\n|\r|\n/)) >= 0) {
      const line = this.buffer.slice(0, idx);
      const sepLen = this.buffer[idx] === "\r" && this.buffer[idx + 1] === "\n" ? 2 : 1;
      this.buffer = this.buffer.slice(idx + sepLen);
      if (line === "") {
        if (this.data.length > 0) emit({ id: this.id, event: this.event || "message", data: this.data.join("\n") });
        this.data = [];
        this.event = "";
        continue;
      }
      if (line.startsWith(":")) continue;
      const colon = line.indexOf(":");
      const field = colon === -1 ? line : line.slice(0, colon);
      let value = colon === -1 ? "" : line.slice(colon + 1);
      if (value.startsWith(" ")) value = value.slice(1);
      if (field === "data") this.data.push(value);
      else if (field === "event") this.event = value;
      else if (field === "id") this.id = value.includes("\0") ? this.id : value;
      else if (field === "retry" && /^\d+$/.test(value)) onRetry?.(Number(value));
    }
  }
}

export interface EventStreamOptions {
  onEvent: (event: EventEnvelope) => void;
  onStatus: (status: StreamStatus, info: { attempt: number; retryInMs: number | null }) => void;
  /** Called for any received bytes (events or heartbeats) — "last confirmed" moment. */
  onActivity: () => void;
  initialCursor?: string | null;
  baseDelayMs?: number;
  maxDelayMs?: number;
}

export class EventStream {
  private controller: AbortController | null = null;
  private cursor: string | null;
  private attempt = 0;
  private timer: number | null = null;
  private stopped = false;
  private serverRetryMs: number | null = null;
  private readonly seen = new Set<string>();
  private readonly seenOrder: string[] = [];

  constructor(private readonly options: EventStreamOptions) {
    this.cursor = options.initialCursor ?? null;
  }

  get lastCursor(): string | null {
    return this.cursor;
  }

  start(): void {
    this.stopped = false;
    void this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = null;
    this.controller?.abort();
    this.controller = null;
  }

  /** Reconnect immediately (e.g. browser came back online). */
  reconnectNow(): void {
    if (this.stopped) return;
    if (this.timer !== null) window.clearTimeout(this.timer);
    this.timer = null;
    this.controller?.abort();
    void this.connect();
  }

  private remember(id: string): boolean {
    if (this.seen.has(id)) return false;
    this.seen.add(id);
    this.seenOrder.push(id);
    if (this.seenOrder.length > 2000) this.seen.delete(this.seenOrder.shift()!);
    return true;
  }

  private async connect(): Promise<void> {
    if (this.stopped) return;
    this.options.onStatus(this.attempt === 0 ? "connecting" : "reconnecting", { attempt: this.attempt, retryInMs: null });
    const controller = new AbortController();
    this.controller = controller;
    const headers: Record<string, string> = { accept: "text/event-stream" };
    if (this.cursor) headers["last-event-id"] = this.cursor;
    try {
      const response = await fetch(buildUrl("/events"), {
        credentials: "same-origin",
        headers,
        signal: controller.signal,
        cache: "no-store",
      });
      if (!response.ok || !response.body) throw new Error(`SSE HTTP ${response.status}`);
      this.attempt = 0;
      this.options.onStatus("live", { attempt: 0, retryInMs: null });
      this.options.onActivity();
      const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
      const parser = new SseParser();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        this.options.onActivity();
        parser.push(
          value,
          (e) => this.dispatch(e),
          (ms) => {
            this.serverRetryMs = ms;
          },
        );
      }
      throw new Error("SSE stream closed");
    } catch (error) {
      if (this.stopped || (error instanceof DOMException && error.name === "AbortError" && this.controller !== controller)) return;
      if (this.stopped) return;
      this.scheduleReconnect();
    }
  }

  private dispatch(e: SseParsedEvent): void {
    if (e.id) this.cursor = e.id;
    if (e.event === "heartbeat" || e.event === "ping") return;
    let parsed: EventEnvelope;
    try {
      parsed = JSON.parse(e.data) as EventEnvelope;
    } catch {
      return;
    }
    const key = parsed.event_id ?? e.id ?? null;
    // Cursor resume may replay the boundary event; never apply the same event twice.
    if (key && !this.remember(key)) return;
    if (!parsed.cursor && e.id) parsed = { ...parsed, cursor: e.id };
    this.options.onEvent(parsed);
  }

  private scheduleReconnect(): void {
    this.attempt += 1;
    const base = this.options.baseDelayMs ?? 1000;
    const max = this.options.maxDelayMs ?? 30_000;
    const exp = Math.min(max, base * 2 ** Math.min(this.attempt - 1, 8));
    const delay = Math.max(this.serverRetryMs ?? 0, Math.round(exp / 2 + Math.random() * (exp / 2)));
    const status: StreamStatus = navigator.onLine === false ? "offline" : "reconnecting";
    this.options.onStatus(status, { attempt: this.attempt, retryInMs: delay });
    this.timer = window.setTimeout(() => {
      this.timer = null;
      void this.connect();
    }, delay);
  }
}
